import { TEvent } from "./types";
import { eventPublisher } from "./publisher";

/**
 * Interface for tracking adapters
 */
export interface TrackingAdapter {
  /**
   * Name of the tracking adapter
   */
  name: string;

  /**
   * Whether the adapter is enabled
   */
  isEnabled: () => boolean;

  /**
   * Initialize the adapter
   */
  initialize: () => Promise<void>;

  /**
   * Track an event
   */
  trackEvent: (event: TEvent) => Promise<void>;
}

/**
 * EventSubscriber class responsible for managing tracking adapters and forwarding events
 */
class EventSubscriber {
  private static instance: EventSubscriber;
  private adapters: TrackingAdapter[] = [];
  private initialized = false;
  private eventQueue: TEvent[] = [];
  private unsubscribe: (() => void) | null = null;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get the singleton instance of EventSubscriber
   */
  public static getInstance(): EventSubscriber {
    if (!EventSubscriber.instance) {
      EventSubscriber.instance = new EventSubscriber();
    }
    return EventSubscriber.instance;
  }

  /**
   * Register a tracking adapter
   * @param adapter The tracking adapter to register
   */
  public registerAdapter(adapter: TrackingAdapter): void {
    // Check if adapter with the same name already exists
    const existingAdapter = this.adapters.find((a) => a.name === adapter.name);
    if (existingAdapter) {
      console.warn(
        `Tracking adapter with name "${adapter.name}" is already registered. Skipping.`
      );
      return;
    }

    this.adapters.push(adapter);

    // If already initialized, initialize the new adapter
    if (this.initialized) {
      this.initializeAdapter(adapter).catch((error) => {
        console.error(`Failed to initialize adapter "${adapter.name}":`, error);
      });
    }
  }

  /**
   * Unregister a tracking adapter by name
   * @param adapterName The name of the adapter to unregister
   */
  public unregisterAdapter(adapterName: string): void {
    this.adapters = this.adapters.filter(
      (adapter) => adapter.name !== adapterName
    );
  }

  /**
   * Initialize all registered adapters
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Subscribe to events
    this.unsubscribe = eventPublisher.subscribe(this.handleEvent.bind(this));

    // Initialize all adapters
    await Promise.all(
      this.adapters.map((adapter) => this.initializeAdapter(adapter))
    );

    this.initialized = true;

    // Process any queued events
    this.processEventQueue();
  }

  /**
   * Initialize a single adapter
   * @param adapter The adapter to initialize
   */
  private async initializeAdapter(adapter: TrackingAdapter): Promise<void> {
    try {
      await adapter.initialize();
      console.log(
        `Tracking adapter "${adapter.name}" initialized successfully.`
      );
    } catch (error) {
      console.error(`Failed to initialize adapter "${adapter.name}":`, error);
    }
  }

  /**
   * Handle an event by forwarding it to all enabled adapters
   * @param event The event to handle
   */
  private handleEvent(event: TEvent): void {
    if (!this.initialized) {
      // Queue the event for later processing
      this.eventQueue.push(event);
      return;
    }

    // Forward the event to all enabled adapters
    this.adapters.forEach((adapter) => {
      if (adapter.isEnabled()) {
        adapter.trackEvent(event).catch((error) => {
          console.error(
            `Error tracking event in adapter "${adapter.name}":`,
            error
          );
        });
      }
    });
  }

  /**
   * Process any events that were queued before initialization
   */
  private processEventQueue(): void {
    if (this.eventQueue.length === 0) {
      return;
    }

    console.log(`Processing ${this.eventQueue.length} queued events.`);

    // Process each queued event
    this.eventQueue.forEach((event) => {
      this.handleEvent(event);
    });

    // Clear the queue
    this.eventQueue = [];
  }

  /**
   * Shutdown the subscriber and all adapters
   */
  public shutdown(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    this.initialized = false;
    this.eventQueue = [];
  }

  /**
   * Get all registered adapters
   */
  public getAdapters(): TrackingAdapter[] {
    return [...this.adapters];
  }
}

// Export singleton instance
export const eventSubscriber = EventSubscriber.getInstance();
